/**
 * contentLoader.js
 *
 * Loads content .md files from src/content/* at build time using webpack's
 * require.context. No file server needed — works with react-scripts out of the box.
 *
 * To add a new project:  drop a .md file in src/content/projects/
 * To add a new trip:     drop a .md file in src/content/travels/
 * To add a career entry: drop a .md file in src/content/career/
 *
 * Frontmatter is parsed with a lightweight inline parser (no Node.js deps).
 * Body is rendered to HTML with marked.
 */

import { marked } from 'marked';

// Configure marked for safe, clean output
marked.setOptions({
  breaks: true,  // line breaks become <br>
  gfm: true,     // GitHub Flavored Markdown
});

/**
 * Lightweight YAML frontmatter parser.
 * Handles strings, numbers, booleans, arrays (- item syntax), and quoted strings.
 * Does NOT support nested objects — keep frontmatter flat.
 *
 * @param {string} raw - raw file contents
 * @returns {{ data: object, content: string }}
 */
function parseFrontmatter(raw) {
  const str = raw.trim();
  if (!str.startsWith('---')) {
    return { data: {}, content: str };
  }

  const end = str.indexOf('\n---', 3);
  if (end === -1) {
    return { data: {}, content: str };
  }

  const yamlBlock = str.slice(3, end).trim();
  const content = str.slice(end + 4).trim();

  const data = {};
  const lines = yamlBlock.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) { i++; continue; }

    const key = line.slice(0, colonIdx).trim();
    const rest = line.slice(colonIdx + 1).trim();

    // Array value — next lines start with '  - '
    if (rest === '' && i + 1 < lines.length && lines[i + 1].trimStart().startsWith('- ')) {
      const arr = [];
      i++;
      while (i < lines.length && lines[i].trimStart().startsWith('- ')) {
        const itemRaw = lines[i].trimStart().slice(2).trim();
        // Check if this item line itself contains "label:" (format: "- label: X")
        if (itemRaw.startsWith('label:')) {
          const obj = {};
          obj.label = itemRaw.slice('label:'.length).trim().replace(/^["']|["']$/g, '');
          // href is on the next line as "    href: ..."
          if (i + 1 < lines.length && lines[i + 1].trimStart().startsWith('href:')) {
            obj.href = lines[i + 1].trimStart().slice('href:'.length).trim().replace(/^["']|["']$/g, '');
            i++;
          }
          arr.push(obj);
        // Legacy format: "- " then next line has "label:"
        } else if (i + 1 < lines.length && lines[i + 1].trimStart().startsWith('label:')) {
          const obj = {};
          obj.label = lines[i + 1].trimStart().slice('label:'.length).trim().replace(/^["']|["']$/g, '');
          if (i + 2 < lines.length && lines[i + 2].trimStart().startsWith('href:')) {
            obj.href = lines[i + 2].trimStart().slice('href:'.length).trim().replace(/^["']|["']$/g, '');
            i += 2;
          } else {
            i++;
          }
          arr.push(obj);
        } else {
          arr.push(parseValue(itemRaw));
        }
        i++;
      }
      data[key] = arr;
      continue;
    }

    // Inline array: [a, b, c]
    if (rest.startsWith('[')) {
      const arrStr = rest.slice(1, rest.lastIndexOf(']'));
      data[key] = arrStr
        .split(',')
        .map((s) => s.trim().replace(/^["']|["']$/g, ''))
        .filter(Boolean);
      i++;
      continue;
    }

    data[key] = parseValue(rest);
    i++;
  }

  return { data, content };
}

function parseValue(str) {
  const s = str.trim();
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s === 'null' || s === '~') return null;
  // Quoted string
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  // Number
  const n = Number(s);
  if (!isNaN(n) && s !== '') return n;
  return s;
}

/**
 * Parse a raw markdown string (including frontmatter) into a JS object.
 * @param {string} raw  - raw file contents as string
 * @returns {{ data: object, html: string }}
 */
function parseMarkdown(raw) {
  const str = typeof raw === 'string' ? raw : String(raw || '');
  const { data, content } = parseFrontmatter(str);
  const html = marked(content);
  return { data, html };
}

/**
 * Load all project markdown files.
 * @returns {Array} sorted array of project objects
 */
export function loadProjects() {
  const context = require.context('../content/projects', false, /\.js$/);
  const projects = context.keys().map((key) => {
    const mod = context(key);
    const raw = typeof mod === 'string' ? mod : (mod.default || '');
    const { data, html } = parseMarkdown(raw);
    return { ...data, contentHtml: html };
  });

  // Sort: featured first, then by year descending, then by title
  return projects.sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    const ay = typeof a.year === 'number' ? a.year : 0;
    const by2 = typeof b.year === 'number' ? b.year : 0;
    if (by2 !== ay) return by2 - ay;
    return (a.title || '').localeCompare(b.title || '');
  });
}

/**
 * Load a single project by slug.
 */
export function loadProjectBySlug(slug) {
  return loadProjects().find((p) => p.slug === slug) || null;
}

/**
 * Load all travel markdown files.
 * @returns {Array} sorted array of trip objects
 */
export function loadTrips() {
  const context = require.context('../content/travels', false, /\.js$/);
  const trips = context.keys().map((key) => {
    const mod = context(key);
    const raw = typeof mod === 'string' ? mod : (mod.default || '');
    const { data, html } = parseMarkdown(raw);
    return { ...data, contentHtml: html };
  });

  const continentOrder = ['Europe', 'Asia', 'Africa', 'North America', 'South America', 'Oceania'];
  return trips.sort((a, b) => {
    const ca = continentOrder.indexOf(a.continent);
    const cb = continentOrder.indexOf(b.continent);
    if (ca !== cb) return (ca === -1 ? 99 : ca) - (cb === -1 ? 99 : cb);
    const ya = typeof a.year === 'number' ? a.year : 9999;
    const yb = typeof b.year === 'number' ? b.year : 9999;
    return yb - ya;
  });
}

/**
 * Load a single trip by slug.
 */
export function loadTripBySlug(slug) {
  return loadTrips().find((t) => t.slug === slug) || null;
}

/**
 * Load all career markdown files.
 * @returns {Array} sorted array (current first, then reverse chronological)
 */
export function loadCareer() {
  const context = require.context('../content/career', false, /\.js$/);
  const entries = context.keys().map((key) => {
    const mod = context(key);
    const raw = typeof mod === 'string' ? mod : (mod.default || '');
    const { data, html } = parseMarkdown(raw);
    return { ...data, contentHtml: html };
  });

  // Sort key: use explicit sortKey if set, otherwise derive from last year in dateRange
  const getSortKey = (e) => {
    if (e.sortKey) return e.sortKey;
    const years = (e.dateRange || '').match(/\d{4}/g);
    const last = years ? years[years.length - 1] : '0000';
    return `${last}-00`;
  };

  return entries.sort((a, b) => {
    if (a.current && !b.current) return -1;
    if (!a.current && b.current) return 1;
    return getSortKey(b).localeCompare(getSortKey(a));
  });
}

/**
 * Load a single career entry by slug.
 */
export function loadCareerBySlug(slug) {
  return loadCareer().find((e) => e.slug === slug) || null;
}
